/**
 * The berth asset list feature (M8 plan MC-b6b; the career in the Reach only): the dashboard's **Fleet › Berths**
 * section (dashboard spec §2.2, W3 card M2; `registerDashSection`) is a read-only list of every berth the pilot holds,
 * wherever the pilot is (`ui/berth/AssetList.tsx`). There is no station tab and no flight key: storing and taking
 * stay on the docked station's Berth tab (MC-b6a), so the station tab walk is unchanged.
 *
 * It reads the Career, the docked station and game time from the `m2` service, and the cached system view from the
 * strategic client (looked up lazily: the strategic link provides it after this feature's init), matching each berth
 * to its view the way the Berth tab does (`berthViewOf`).
 */
import { FEATURE_ORDER, type GameContext, type GameFeature } from '../../app/feature'
import type { StationView } from '../../strategic/types'
import { createAssetListSection, type AssetListSource } from '../../ui/berth/AssetList'
import { openDashboard, registerDashSection } from '../../ui/dashboard/registry'
import { fleetSection, FLEET_SECTION_IDS } from '../../ui/fleet/sectionHost'
import type { BerthAssetsInput } from '../../ui/models/berthAssets'
import { berthViewOf } from '../berth/feature'

/** After the Logs screen (charter HUD + 3), clear of the wing HUD (535). */
const ORDER = FEATURE_ORDER.charterHud + 4

export interface BerthAssetsFeature extends GameFeature {
  /** True once the Fleet › Berths section is registered (a career in the Reach). */
  readonly registered: boolean
  /** Opens the dashboard at Fleet › Berths (the debug API). */
  openAssets(): void
  dispose(): void
}

/** The list's reads over the career runtime and the strategic client. */
export function berthAssetsSource(ctx: GameContext): AssetListSource {
  const views = (): readonly StationView[] => ctx.services.maybe('strategic')?.systemView?.stations ?? []
  return {
    input(): BerthAssetsInput | null {
      const m2 = ctx.services.maybe('m2')
      if (m2 === undefined) return null
      const ui = m2.ui.getState()
      if (ui.career === null) return null
      const stations = views()
      const layout = ctx.reach?.session.layout ?? null
      return {
        data: m2.data,
        career: ui.career,
        dockedAt: ui.stationId,
        viewOf: (id) => berthViewOf(id, stations, layout),
        stations,
        now: ui.gameTime,
      }
    },
    subscribe(fn): () => void {
      const offs: (() => void)[] = []
      const m2 = ctx.services.maybe('m2')
      if (m2 !== undefined) {
        offs.push(
          m2.ui.subscribe((s, prev) => {
            if (s.career !== prev.career || s.stationId !== prev.stationId) fn()
          }),
        )
      }
      const client = ctx.services.maybe('strategic')
      if (client !== undefined) offs.push(client.onSystemView(() => fn()))
      return () => {
        for (const off of offs.splice(0)) off()
      }
    },
  }
}

export function createBerthAssetsFeature(): BerthAssetsFeature {
  const offs: (() => void)[] = []

  const feature: BerthAssetsFeature = {
    name: 'berthAssets',
    order: ORDER,
    get registered(): boolean {
      return offs.length > 0
    },

    init(ctx: GameContext): void {
      if (ctx.reach === null || ctx.reach === undefined || ctx.mode !== 'career') return
      offs.push(registerDashSection(fleetSection('berths', createAssetListSection(berthAssetsSource(ctx)))))
    },

    openAssets(): void {
      openDashboard({ tab: 'fleet', section: FLEET_SECTION_IDS.berths })
    },

    dispose(): void {
      for (const off of offs.splice(0)) off()
    },
  }
  return feature
}

const feature: BerthAssetsFeature = createBerthAssetsFeature()

export default feature
